/**
 * API utilities for communicating with the backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://launch-post-api.onrender.com'

/**
 * Stream chat completion from the backend
 * @param {Array} messages - Array of message objects with role and content
 * @param {Function} onChunk - Callback function called with each text chunk
 * @param {string} agent - Agent ID to use (e.g., 'launch-post')
 * @returns {Promise<void>}
 */
export async function streamChatCompletion(messages, onChunk, agent = 'launch-post') {
  const response = await fetch(`${API_BASE_URL}/api/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ messages, agent })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    
    if (response.status === 401) {
      throw new Error('API key is invalid or expired.')
    } else if (response.status === 429) {
      throw new Error('Rate limit exceeded. Please wait a moment.')
    } else if (response.status >= 500) {
      throw new Error('Service is temporarily unavailable.')
    } else {
      throw new Error(errorData.error?.message || `Request failed with status ${response.status}`)
    }
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()

  try {
    while (true) {
      const { done, value } = await reader.read()
      
      if (done) break

      const chunk = decoder.decode(value, { stream: true })
      const lines = chunk.split('\n')

      for (const line of lines) {
        // Skip empty lines and comments
        if (!line.trim() || line.startsWith(':')) continue
        
        // Remove "data: " prefix
        if (line.startsWith('data: ')) {
          const data = line.slice(6)
          
          // Check for stream end signal
          if (data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)
            const content = parsed.choices?.[0]?.delta?.content
            
            if (content) {
              onChunk(content)
            }
          } catch (parseError) {
            // Skip malformed JSON chunks
            console.debug('Skipping malformed chunk:', data)
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}
